import React from "react";
import { useAuth } from "@/App";
import {
  LayoutDashboard, FileText, Truck, Users, Car, Fuel, Receipt, LogOut, ChevronLeft, ChevronRight, MessageSquare, UserCog
} from "lucide-react";
import { useState } from "react";

const navItems = [
  { path: "/app", label: "Dashboard", icon: LayoutDashboard },
  { path: "/app/jobs", label: "Jobs", icon: Truck },
  { path: "/app/invoices", label: "Invoices", icon: FileText },
  { path: "/app/drivers", label: "Drivers", icon: Users },
  { path: "/app/vehicles", label: "Vehicles", icon: Car },
  { path: "/app/fuel", label: "Fuel Logs", icon: Fuel },
  { path: "/app/expenses", label: "Expenses", icon: Receipt },
  { path: "/app/inquiries", label: "Inquiries", icon: MessageSquare },
  { path: "/app/team", label: "Team", icon: UserCog },
];

export default function Sidebar({ currentPath, onNavigate }) {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      data-testid="sidebar"
      className={`${collapsed ? "w-16" : "w-56"} h-screen flex flex-col bg-[#0f0f12] border-r border-zinc-800/60 transition-all duration-200`}
    >
      {/* Logo */}
      <div className="h-14 flex items-center gap-2.5 px-4 border-b border-zinc-800/60">
        <img
          src="https://customer-assets.emergentagent.com/job_dispatch-pro-75/artifacts/edmtbm97_26023.jpg"
          alt="SGHK Logistics"
          className="w-8 h-8 rounded-sm object-contain flex-shrink-0"
        />
        {!collapsed && (
          <span className="font-heading text-sm font-bold text-white tracking-tight whitespace-nowrap">
            SGHK LOGISTICS
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = currentPath === item.path || (item.path !== "/" && currentPath.startsWith(item.path));
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              onClick={() => onNavigate(item.path)}
              className={`w-full flex items-center gap-3 px-3 h-9 rounded-sm text-sm font-medium transition-colors
                ${isActive
                  ? "bg-orange-500/10 text-orange-500"
                  : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50"
                }
              `}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* Collapse Toggle */}
      <button
        data-testid="sidebar-toggle"
        onClick={() => setCollapsed(!collapsed)}
        className="h-9 flex items-center justify-center text-zinc-500 hover:text-zinc-300 border-t border-zinc-800/60"
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      {/* User */}
      <div className="p-3 border-t border-zinc-800/60">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-sm bg-zinc-800 flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-zinc-400">{user?.name?.[0]?.toUpperCase() || "U"}</span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-zinc-300 truncate">{user?.name}</p>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{user?.role}</p>
            </div>
          )}
          <button
            data-testid="logout-button"
            onClick={logout}
            className="text-zinc-500 hover:text-red-400 transition-colors flex-shrink-0"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
